import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import Dinero from 'dinero.js'
import { formOptions } from 'utils/validation/purchaseOrder'
import { PAYMENT_METHODS_VALUES, PURCHASE_ORDER_DROPDOWN_VALUES, MEASURE_UNIT } from 'utils/constants'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'
import { useGetPriceSheetQuery } from 'services/priceSheets'
import { useGetPurchaseOrderQuery } from 'services/purchaseOrders'
import { useGetCustomerQuery } from 'services/customers'
import ContractContainerStatic from '../ContractContainerStatic'
import usePaymentTerms from 'utils/custom-hooks/usePaymentTerms'

const { PACKAGE_OPTIONS, PICKUP_OPTIONS, PAYMENT_OPTIONS_OPTIONS } = PURCHASE_ORDER_DROPDOWN_VALUES

const PurchaseOrderContainer = () => {
  const methods = useForm(formOptions)
  const { id: purchaseOrderId } = useParams()
  const [customerId, setCustomerId] = useState('')
  const [initialMaterials, setInitialMaterials] = useState([])
  const { defaultCurrency, defaultYardUm } = useSelector(state => state.settings)
  const { paymentTermsOptions } = usePaymentTerms({})

  const { data: purchaseOrderData, isLoading: isLoadingPurchaseOrder } = useGetPurchaseOrderQuery({ purchaseOrderId })
  const { data: customerData, isLoading: isLoadingCustomerData } = useGetCustomerQuery(
    { customerId },
    { refetchOnMountOrArgChange: true, skip: !customerId },
  )
  const priceSheetFilter = {}
  if (purchaseOrderId) priceSheetFilter.showArchived = true
  const { data: priceSheetData, isLoading: isLoadingPriceSheet } = useGetPriceSheetQuery(
    { priceSheetId: purchaseOrderData?.priceSheetId, filter: JSON.stringify(priceSheetFilter) },
    { skip: !purchaseOrderData },
  )

  const isLoading = isLoadingPriceSheet || isLoadingPurchaseOrder || isLoadingCustomerData || false

  useGlobalLoader(isLoading)

  useEffect(() => {
    // map purchaseOrderData into correct formatting
    if (purchaseOrderData) {
      setCustomerId(purchaseOrderData?.supplierId)
    }

    if (purchaseOrderData && customerData) {
      methods.reset({
        supplier: purchaseOrderData.supplier?.displayName || ' ',
        customerSONumber: purchaseOrderData.customerSONumber || ' ',
        purchaseOrderLocationId:
          customerData?.locations?.find(({ id }) => id === purchaseOrderData?.purchaseOrderLocationId)?.name || ' ',
        purchaseOrderTicketId: purchaseOrderData?.purchaseOrderTicketId || ' ',
        order: {
          effectiveDate: dateFormatter(purchaseOrderData?.order?.effectiveDate),
          expirationDate: dateFormatter(purchaseOrderData?.order?.expirationDate),
          packageType: PACKAGE_OPTIONS.find(option => option.id === purchaseOrderData?.order?.packageType)?.name || ' ',
          pickupDelivery:
            PICKUP_OPTIONS.find(option => option.id === purchaseOrderData?.order?.pickupDelivery)?.name || ' ',
          receivingYard: purchaseOrderData?.order?.receivingYard || ' ',
        },
        currency: purchaseOrderData?.currency || purchaseOrderData?.supplier?.defaultCurrency || defaultCurrency || ' ',
        paymentTermId: paymentTermsOptions?.find(option => option.id === purchaseOrderData?.paymentTermId)?.name || ' ',
        paymentMethod: PAYMENT_METHODS_VALUES[purchaseOrderData?.paymentMethod?.toUpperCase()] || '',
        paymentOptions:
          PAYMENT_OPTIONS_OPTIONS.find(option => option.id === purchaseOrderData?.paymentOptions)?.name || ' ',
        notes: purchaseOrderData.notes,
        docs: purchaseOrderData.docs,
      })
    }
  }, [purchaseOrderData, customerData])

  const formatInitialMaterialData = (materials = []) =>
    materials.map(material => ({
      id: material.id,
      materialTypeId: material.materialTypeId,
      um: (material.weightUnits || defaultYardUm).toUpperCase(),
      targetWeight: material.targetWeight,
      fulfilledWeight: material.fulfilledWeight,
      price: Dinero({ ...material?.buyPrice?.price }).toUnit(),
      materialName: material.materialName,
      description: material?.description,
      overrideName: material.overrideName,
      code: material.materialCode,
    }))

  useEffect(() => {
    setInitialMaterials(formatInitialMaterialData(priceSheetData?.entriesObj))
  }, [isLoadingPriceSheet, isLoadingPurchaseOrder])

  return (
    <ContractContainerStatic
      methods={methods}
      customerId={customerId}
      isLoadingProps={isLoading}
      contractData={purchaseOrderData}
      customerData={customerData}
      initialMaterials={initialMaterials}
    />
  )
}

export default PurchaseOrderContainer
